import React, { FC, useRef, useState } from 'react'
import { Trans, t } from '@lingui/macro'

import { useCPDFeatureFlag } from 'front-end/src/app/hooks/useFeatureFlags'

import Form, { FormRow } from '../../../components/common/form/Form'
import Input from '../../../components/common/form/input/Input'
import Label from '../../../components/common/form/label/Label'
import {
  ButtonPrimary,
  ButtonClean,
  Switch,
  HStack
} from 'front-end/src/components'

import { IGroup } from '../../../../../types/Group'
import { Modal } from '../../../components/common/modal/Modal'
import { useCreateGroupMutation } from './GroupCreateModal.codegen'
import { GroupsWithUserCountsDocument } from '../../manage-campaign-page/components/campaign-audience/filter-humanizers/GroupFilterHumanizer.codegen'

export type PartialGroup = Pick<IGroup, 'id' | 'name'>

interface IProps {
  show: boolean
  onGroupCreated: (group: PartialGroup) => void
  onCancel: () => void
}

export interface IResponse {
  createGroup: {
    id: number
    name: string
  }
}

const GroupCreateModal: FC<IProps> = (props) => {
  const { cpdAlias, isCPDEnabledInAccountSettings } = useCPDFeatureFlag()
  const [name, setName] = useState('')
  const [cpdRequirementAllowed, setCpdRequirementAllowed] =
    useState<boolean>(false)

  const inputEl = useRef(null)
  const [createGroup, res] = useCreateGroupMutation({
    refetchQueries: [{ query: GroupsWithUserCountsDocument }]
  })
  const reset = () => {}

  const handleOnSubmit = async () => {
    const { onGroupCreated } = props

    if (!name) {
      return
    }

    const result = await createGroup({
      variables: { name, isCpd: cpdRequirementAllowed }
    })

    if (!result.data?.admin?.createGroup) {
      return null
    }

    onGroupCreated(result.data.admin?.createGroup as PartialGroup)
  }

  const renderControls = () => {
    const { onCancel } = props

    return (
      <>
        <ButtonClean onClick={onCancel}>
          <Trans>Cancel</Trans>
        </ButtonClean>
        <ButtonPrimary
          disabled={!name}
          onClick={handleOnSubmit}
          isLoading={res.loading}
        >
          <Trans>Create</Trans>
        </ButtonPrimary>
      </>
    )
  }

  const { show } = props

  return (
    <Modal
      header={t`Create a group`}
      show={show}
      onShown={() => {
        if (inputEl.current) {
          // @ts-expect-error
          inputEl.current.focus()
        }
      }}
      onHidden={() => reset()}
      controls={renderControls()}
    >
      <Form onSubmit={handleOnSubmit}>
        <FormRow>
          <Label htmlFor="createGroupInput">
            <Trans>Group name</Trans>
          </Label>
          <Input
            fullWidth
            id="createGroupInput"
            placeholder={t`My new group`}
            onChange={(val) => {
              setName(val)
            }}
            inputRef={inputEl}
            value={name}
          />
          <hr />

          {isCPDEnabledInAccountSettings && (
            <Label htmlFor="cpdHours">
              <HStack alignItems={'baseline'} spacing="8px" shouldWrapChildren>
                <Trans>Enable {cpdAlias} for this group</Trans>
                <Switch
                  onChange={async () => {
                    setCpdRequirementAllowed(!cpdRequirementAllowed)
                  }}
                  isChecked={cpdRequirementAllowed}
                />
              </HStack>
            </Label>
          )}
        </FormRow>
      </Form>
    </Modal>
  )
}

export default GroupCreateModal
